import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  BookOpen,
  Calendar,
  GraduationCap,
  School,
} from 'lucide-react';
import { Education } from '../../../../../utils/types/JobOpenings';

export interface IEducationTabProps {
  educations?: Education[];
}

interface GroupedEducation {
  university: string;
  degrees: Education[];
}

export const EducationTab = ({ educations }: IEducationTabProps) => {
  const formatDate = (timestamp: number): string | null => {
    if (!timestamp) return null;
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
    });
  };

  // Group education by university
  const groupEducationByUniversity = (): GroupedEducation[] => {
    if (!educations || educations.length === 0) return [];

    const grouped = educations.reduce((acc, edu) => {
      const university = edu.university || 'Unknown University';

      if (!acc[university]) {
        acc[university] = [];
      }
      acc[university].push(edu);
      return acc;
    }, {} as Record<string, Education[]>);

    // Convert to array and sort by graduation year (most recent first)
    return Object.entries(grouped)
      .map(([university, degrees]) => {
        const sortedDegrees = degrees.sort(
          (a, b) => (b.graduationYear || 0) - (a.graduationYear || 0)
        );

        return {
          university,
          degrees: sortedDegrees,
        };
      })
      .sort((a, b) => {
        const aLatest = Math.max(...a.degrees.map((d) => d.graduationYear || 0));
        const bLatest = Math.max(...b.degrees.map((d) => d.graduationYear || 0));
        return bLatest - aLatest;
      });
  };

  const groupedEducation = groupEducationByUniversity();

  if (groupedEducation.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          py: 8,
          px: 3,
          textAlign: 'center',
          borderRadius: '16px',
          border: '2px dashed',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          my: 2,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'rgba(147, 51, 234, 0.08)',
            color: '#7e22ce',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <GraduationCap size={28} />
        </Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
        >
          No Education Details
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 300, mx: 'auto' }}>
          No formal education records have been provided for this applicant.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ py: 1.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              p: 0.75,
              borderRadius: '8px',
              bgcolor: 'rgba(147, 51, 234, 0.1)',
              color: '#7e22ce',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <School size={18} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: '15px', color: 'text.primary' }}
          >
            Education & Academic Background
          </Typography>
        </Stack>
        <Chip
          label={`${educations?.length || 0} Record${(educations?.length || 0) !== 1 ? 's' : ''}`}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '11px',
            bgcolor: 'grey.100',
            color: 'text.secondary',
          }}
        />
      </Stack>

      {/* University Groups */}
      <Stack spacing={2.5}>
        {groupedEducation.map((group, groupIdx) => (
          <Paper
            key={groupIdx}
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'grey.400',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {/* University Title Row */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '8px',
                    bgcolor: 'rgba(37, 99, 235, 0.08)',
                    color: '#2563eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <GraduationCap size={18} />
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, fontSize: '14px', color: 'text.primary' }}
                >
                  {group.university}
                </Typography>
              </Stack>
              <Chip
                label={`${group.degrees.length} Degree${group.degrees.length > 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{
                  height: '22px',
                  fontSize: '11px',
                  fontWeight: 600,
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                }}
              />
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Degrees within University */}
            <Stack spacing={2}>
              {group.degrees.map((degree, degreeIdx) => (
                <Box
                  key={degreeIdx}
                  sx={{
                    p: 1.5,
                    borderRadius: '8px',
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
                  >
                    {degree.degree || 'Degree'}
                  </Typography>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 2 }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    sx={{ mt: 1 }}
                  >
                    {degree.fieldOfStudy && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <BookOpen size={14} color="#64748b" />
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '12px' }}
                        >
                          {degree.fieldOfStudy}
                        </Typography>
                      </Stack>
                    )}

                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Calendar size={14} color="#64748b" />
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontSize: '12px' }}
                      >
                        Graduated:{' '}
                        <strong style={{ color: '#0f172a' }}>
                          {formatDate(Number(degree.graduationYear)) || degree.graduationYear || 'N/A'}
                        </strong>
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};
